import * as React from "react";
import { Dialog as BaseUIDialog, DialogRootProps } from "@base-ui/react/dialog";
import { Button, ButtonVariant } from "@/components/frontend/ui/button";
import { CardVariant } from "@/components/frontend/ui/card";
import { resolveRelationshipId } from "@/lib/utils/theme-configuration-class";
import { cn } from "@/lib/utils";
import { X } from "lucide-react";
import "./style.scss";

interface DialogProps extends DialogRootProps {
	children: React.ReactNode;
	trigger: React.ReactElement;
	variant?: CardVariant;
	title: string;
}

export const Dialog: React.FC<DialogProps> = ({ children, trigger, variant = "default", title, ...props }) => {
	const variantId = resolveRelationshipId(variant);
	return (
		<BaseUIDialog.Root {...props}>
			<BaseUIDialog.Trigger render={() => trigger} />
			<BaseUIDialog.Portal className={cn("ms-card-" + variantId)}>
				<BaseUIDialog.Backdrop className="fixed z-1000 inset-0 min-h-dvh bg-black opacity-20 transition-all duration-150 data-[ending-style]:opacity-0 data-[starting-style]:opacity-0 dark:opacity-70 supports-[-webkit-touch-callout:none]:absolute" />
				<BaseUIDialog.Popup className="ms-dialog-popup z-1005 fixed top-1/2 left-1/2 -mt-2 sm:-mt-8 w-[48rem] max-w-[calc(100vw-3rem)] -translate-x-1/2 -translate-y-1/2 transition-all duration-150 data-[ending-style]:scale-90 data-[ending-style]:opacity-0 data-[starting-style]:scale-90 data-[starting-style]:opacity-0 dark:outline-gray-300">
					<BaseUIDialog.Title className="sr-only">{title}</BaseUIDialog.Title>
					{children}
				</BaseUIDialog.Popup>
			</BaseUIDialog.Portal>
		</BaseUIDialog.Root>
	);
};

interface DialogSectionProps {
	children: React.ReactNode;
	className?: string;
}

export const DialogHeader: React.FC<DialogSectionProps> = ({ children, className }) => {
	return <div className={cn("ms-dialog-header", className)}>{children}</div>;
};

export const DialogContent: React.FC<DialogSectionProps> = ({ children, className }) => {
	return <div className={cn("ms-dialog-content", className)}>{children}</div>;
};

export const DialogFooter: React.FC<DialogSectionProps> = ({ children, className }) => {
	return <div className={cn("ms-dialog-footer", className)}>{children}</div>;
};

interface DialogCloseButtonProps {
	variant?: ButtonVariant;
	className?: string;
	label?: string;
}

export const DialogCloseButton: React.FC<DialogCloseButtonProps> = ({ variant, className, label = "Close" }) => {
	return (
		<BaseUIDialog.Close>
			<Button type="button" variant={variant} aria-label={label} nativeButton={false} className={cn("p-0", className)}>
				<X className="size-5" aria-hidden />
			</Button>
		</BaseUIDialog.Close>
	);
};
